import { useMemo } from 'react'

import { useCopilotAction, useCopilotContext } from '@copilotkit/react-core'
import { useToolCallId } from '@factorialco/f0-react'
import { useAiChat } from 'modules/ai/chat/useAiChat'

import { DashboardCard } from './components/DashboardCard'
import type { DashboardCanvasContent, DashboardRuntimeConfig } from './components/types'
import { buildDashboardRuntimeConfig, toF0DashboardCanvasContent } from './f0Adapters'

type RenderedDashboardCardProps = {
  config: DashboardRuntimeConfig
  savedDashboardId?: string
  savedDashboardCategory?: string
  savedDashboardDescription?: string
  savedDashboardUnsaved?: boolean
}

/**
 * Internal wrapper rendered inside CopilotKit's action render. Pulls the
 * tool call id and AI chat state via hooks (only allowed inside a React
 * component) and forwards them as plain props to the local DashboardCard.
 */
function RenderedDashboardCard({
  config,
  savedDashboardId,
  savedDashboardCategory,
  savedDashboardDescription,
  savedDashboardUnsaved,
}: RenderedDashboardCardProps) {
  const { copilotApiConfig } = useCopilotContext()
  const { canvasContent, openCanvas, closeCanvas, runtimeFetch } = useAiChat()
  const toolCallId = useToolCallId()

  const apiConfig = useMemo(
    () => ({
      baseUrl: copilotApiConfig.chatApiEndpoint,
      headers: copilotApiConfig.headers,
      runtimeFetch,
    }),
    [copilotApiConfig.chatApiEndpoint, copilotApiConfig.headers, runtimeFetch]
  )

  const isActive =
    canvasContent?.type === 'dashboard' &&
    canvasContent.toolCallId != null &&
    canvasContent.toolCallId === toolCallId

  return (
    <DashboardCard
      config={config}
      apiConfig={apiConfig}
      savedDashboardId={savedDashboardId}
      savedDashboardCategory={savedDashboardCategory}
      savedDashboardDescription={savedDashboardDescription}
      savedDashboardUnsaved={savedDashboardUnsaved}
      cardId={toolCallId}
      isActive={isActive}
      onOpen={(resolved) => {
        const nextContent: DashboardCanvasContent = {
          type: 'dashboard',
          title: resolved.config.title,
          config: resolved.config,
          apiConfig,
          toolCallId,
          savedDashboardId: resolved.savedDashboardId,
          savedDashboardCategory: resolved.savedDashboardCategory,
          savedDashboardDescription: resolved.savedDashboardDescription,
          savedDashboardUnsaved: resolved.savedDashboardUnsaved,
        }

        openCanvas(toF0DashboardCanvasContent(nextContent))
      }}
      onClose={closeCanvas}
    />
  )
}

/**
 * Display a data-driven report dashboard in a canvas panel. Data is computed
 * server-side via the dashboard compute endpoint. The agent triggers this
 * frontend tool after gathering data via multiple `queryData` calls; the
 * backend reads stored fetchSpecs from requestContext and injects them into
 * the emitFrontendTool payload.
 *
 * An inline report card is rendered in the chat. New dashboards auto-open in
 * the canvas panel. The card stays for re-opening later.
 */
export const useDisplayDashboardAction = () => {
  useCopilotAction({
    name: 'displayDashboard',
    description:
      'Display a data-driven report dashboard in a canvas panel. Data is computed server-side via the dashboard compute endpoint.',
    parameters: [
      {
        name: 'title',
        type: 'string',
        description: 'Dashboard title displayed in the canvas header',
        required: true,
      },
      {
        name: 'description',
        type: 'string',
        description:
          'AI-generated 1–2 sentence summary of what the dashboard shows. Rendered under the title in the canvas header. Lands on ChatDashboardConfig.description.',
        required: false,
      },
      {
        name: 'items',
        type: 'object[]',
        description:
          'Ordered list of dashboard items. Each item has a type (chart, metric, or collection) with computation specs referencing datasets.',
        required: true,
        attributes: [
          {
            name: 'id',
            type: 'string',
            description: 'Unique identifier for the item',
            required: true,
          },
          {
            name: 'type',
            type: 'string',
            description: 'Item type: "chart", "metric", or "collection"',
            required: true,
          },
          {
            name: 'title',
            type: 'string',
            description: 'Item title displayed in the card header',
            required: true,
          },
          {
            name: 'description',
            type: 'string',
            description: 'Optional item description',
            required: false,
          },
          {
            name: 'colSpan',
            type: 'number',
            description: 'Deprecated — items auto-size in equal-width rows. Leave unset.',
            required: false,
          },
          {
            name: 'computation',
            type: 'object',
            description: 'Declarative computation spec (datasetId, aggregation, axes, etc.)',
            required: true,
          },
        ],
      },
      {
        name: 'filters',
        type: 'object',
        description:
          'Filter definitions keyed by filter ID. Supported types are `in` for multi-select values, `numericRange` for min/max number filters, and `date` for date-range filters.',
        required: false,
      },
      {
        name: 'fetchSpecs',
        type: 'object',
        description:
          'Fetch specifications for server-side data retrieval. Keyed by datasetId, each describes how to fetch and query data.',
        required: false,
      },
      {
        name: 'navigationFilters',
        type: 'object',
        description:
          'Dashboard-level navigation filter definitions keyed by filter ID (e.g. a `dateNavigation` pill rendered above the grid). Forwarded to ChatDashboardConfig so F0AnalyticsDashboard renders the pill and emits navigationFilterValues into `/dashboard/compute`.',
        required: false,
      },
      {
        name: 'savedDashboardId',
        type: 'string',
        description:
          'ID of a pre-saved dashboard. Present only when iterating on an existing saved dashboard.',
        required: false,
      },
      {
        name: 'savedDashboardCategory',
        type: 'string',
        description: 'Category of the saved dashboard.',
        required: false,
      },
      {
        name: 'savedDashboardDescription',
        type: 'string',
        description: 'Description of the saved dashboard.',
        required: false,
      },
      {
        name: 'savedDashboardUnsaved',
        type: 'boolean',
        description:
          'Whether the dashboard has unsaved changes (e.g. agent iteration not yet persisted).',
        required: false,
      },
      {
        name: 'snapshot',
        type: 'boolean',
        description:
          '`true` for "as of <date>" dashboards. Forwarded to `/dashboard/compute` so the backend suppresses the date navigator and rewrites snapshot-aware fetch args.',
        required: false,
      },
      {
        name: 'snapshotDate',
        type: 'string',
        description:
          'ISO-8601 calendar date the snapshot is keyed to. Required when `snapshot: true`.',
        required: false,
      },
      {
        name: 'dateNavigatorColumn',
        type: 'string',
        description:
          'Agent-named DATE column to promote as the navigator pill. Forwarded to `/dashboard/compute` so the pill is emitted; when omitted, every DATE column flows into the Filters drawer.',
        required: false,
      },
    ],
    available: 'frontend',
    render: ({
      args: {
        title,
        description,
        items,
        filters,
        fetchSpecs,
        navigationFilters,
        savedDashboardId,
        savedDashboardCategory,
        savedDashboardDescription,
        savedDashboardUnsaved,
        snapshot,
        snapshotDate,
        dateNavigatorColumn,
      },
    }) => {
      if (!title || !items || items.length === 0) {
        return <></>
      }

      const config = buildDashboardRuntimeConfig({
        title,
        description,
        items,
        filters,
        fetchSpecs,
        navigationFilters,
        snapshot,
        snapshotDate,
        dateNavigatorColumn,
      })
      if (!config) return <></>

      return (
        <RenderedDashboardCard
          config={config}
          savedDashboardId={savedDashboardId}
          savedDashboardCategory={savedDashboardCategory}
          savedDashboardDescription={savedDashboardDescription}
          savedDashboardUnsaved={savedDashboardUnsaved}
        />
      )
    },
  })
}
